import {
  IconButton,
  LoadingSpinner,
  Modal,
  formatCurrency,
} from "@scandotcom/react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import { IconRapid } from "../../Icons";
import { formatISOString, isNil } from "../../utils";
import { NextDayModal } from "../NextDayModal";
import type { LocationResult, ScanType } from "../types";
import { AvailabilityItem } from "./AvailabilityItem";
import { ConfirmationModal } from "./ConfirmationModal";
import {
  useAvailabilitySlots,
  type AvailabilitySlot,
} from "./useAvailabilitySlots";

import { Transition } from "@headlessui/react";
import classNames from "classnames";
import { format, isTomorrow, parse, parseISO } from "date-fns";
import * as A from "fp-ts/Array";
import * as NEA from "fp-ts/NonEmptyArray";
import * as Ord from "fp-ts/Ord";
import * as R from "fp-ts/Record";
import { pipe } from "fp-ts/lib/function";
import { Ord as ordString } from "fp-ts/string";
import Bugsnag from "@bugsnag/js";
import { getGoogleAppointmentsToken } from "../../lib/googleAppointments";
import {
  BOOKING_ERROR_MESSAGE,
  BookingRejectedError,
  createDraftOrder,
} from "../createDraftOrder";
import { AppointmentInfoModal } from "./AppointmentInfo";

interface Props {
  scanningCenter: Pick<
    LocationResult,
    "id" | "name" | "next_day_surcharge" | "price"
  >;
  selectedScanType: ScanType;
  selectedBodyPartIds: number[];
  searchId: string;
  onClose: () => void;
  daySelected?: string;
  hidePrices?: boolean;
}

interface State {
  confirmationModalVisible: boolean;
  selectedSlot: AvailabilitySlot | null;
}

type Dispatch =
  | { type: "ShowConfirmationModal"; slot: AvailabilitySlot }
  | { type: "HideConfirmationModal" };

const reducer: React.Reducer<State, Dispatch> = (state, action): State => {
  if (action.type === "ShowConfirmationModal") {
    return {
      ...state,
      selectedSlot: action.slot,
      confirmationModalVisible: true,
    };
  }
  if (action.type === "HideConfirmationModal") {
    return { ...state, confirmationModalVisible: false };
  }
  return state;
};

const initialState: State = {
  selectedSlot: null,
  confirmationModalVisible: false,
};

const DAY_FORMAT = "yyyy-LL-dd";

/**
 * Given an array of slots, group them by day to produce an array
 * of day / list-of-slots pairs, sorted by day.
 * @param slots
 * @returns
 */
const processSlots = (slots: AvailabilitySlot[]) => {
  const bySlotDay = pipe(
    ordString,
    Ord.contramap(
      ([day, _slots]: [day: string, _slots: AvailabilitySlot[]]) => day
    )
  );

  return pipe(
    slots,
    NEA.groupBy((slot) => format(parseISO(slot.start_time), DAY_FORMAT)),
    R.toArray,
    A.sort(bySlotDay)
  );
};

const dateStyles = "text-neutral-900 font-semibold text-sm";

export const AvailabilityModalContent = ({
  scanningCenter,
  selectedScanType,
  selectedBodyPartIds,
  searchId,
  onClose,
  daySelected,
  hidePrices,
}: Props) => {
  const [showNextDayModal, setShowNextDayModal] = useState(false);
  const [showAppointmentInfoModal, setShowAppointmentInfoModal] =
    useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const bookingErrorRef = useRef<HTMLDivElement>(null);

  const [state, dispatch] = React.useReducer(reducer, initialState);

  const { slots, dateRange, loading, setPagination, hasPrevPage, refetch } =
    useAvailabilitySlots({
      locationId: scanningCenter.id,
      scanTypeId: selectedScanType.id,
      bodyPartIds: selectedBodyPartIds,
      cursor: daySelected,
    });
  const loadPrevPage = () => setPagination({ before: dateRange?.start ?? "" });
  const loadNextPage = () => setPagination({ from: dateRange?.end ?? "" });

  const slotsByDay = slots ? processSlots(slots) : [];

  const startDate = dateRange
    ? formatISOString(dateRange.start, "dd LLL")
    : null;
  const endDate = dateRange ? formatISOString(dateRange.end, "dd LLL") : null;

  const onConfirm = async () => {
    setBookingError(null);

    try {
      await createDraftOrder({
        scanningLocationId: scanningCenter.id,
        bodyPartIds: selectedBodyPartIds,
        scanTypeId: selectedScanType.id,
        searchId,
        price: scanningCenter.price,
        slotIds: state.selectedSlot?.ids,
        googleAppointmentsToken:
          getGoogleAppointmentsToken(location.search) ?? undefined,
      });
    } catch (error) {
      dispatch({ type: "HideConfirmationModal" });

      if (error instanceof BookingRejectedError) {
        setBookingError(error.message);
      } else {
        Bugsnag.notify(error as Error);
        setBookingError(BOOKING_ERROR_MESSAGE);
      }

      // Reload availability in case the chosen slot was taken, so it drops out of the list. Harmless no-op for the
      // other failure kinds (generic validation, network) where the slot itself is still fine.
      refetch();
    }
  };

  useEffect(() => {
    // Don't yank the scroll back to the selected day while a booking error is showing, or it pushes the inline
    // banner (rendered above the slot list) out of view — the opposite of what we want after a failed booking.
    if (bookingError || !scrollToRef?.current) return;

    scrollToRef.current.scrollIntoView(true);
  });

  useEffect(() => {
    if (bookingError) bookingErrorRef.current?.scrollIntoView(true);
  }, [bookingError]);

  return (
    <>
      <NextDayModal
        isOpen={showNextDayModal}
        onClose={() => setShowNextDayModal(false)}
        nextDaySurcharge={
          hidePrices ? null : scanningCenter.next_day_surcharge ?? null
        }
      />

      <ConfirmationModal
        location={scanningCenter}
        modality={selectedScanType}
        bodyPartIds={selectedBodyPartIds}
        slot={state.selectedSlot}
        isOpen={state.confirmationModalVisible}
        onClose={() => dispatch({ type: "HideConfirmationModal" })}
        onConfirm={onConfirm}
        hidePrices={hidePrices}
      />

      <AppointmentInfoModal
        isOpen={showAppointmentInfoModal}
        onClose={() => setShowAppointmentInfoModal(false)}
      />

      <Modal.Header className="mb-4" onClose={onClose} type="back">
        <span className="sm:mr-auto">Request an appointment</span>
      </Modal.Header>

      <p className="mb-4">
        Appointment requests are subject to safety checks and protocolling.
        Please wait to make travel or time off plans until the scanning centre
        calls to confirm your slot.{" "}
        <button
          className="align-text-bottom text-neutral-500"
          onClick={() => setShowAppointmentInfoModal(true)}
        >
          <span className="sr-only">read more</span>
          <IconInfoCircle height={18} width={18} aria-hidden />
        </button>
      </p>

      {bookingError && (
        <div
          ref={bookingErrorRef}
          role="alert"
          className="bg-warning-50 border-warning-700 text-warning-900 mb-4 rounded-md border px-4 py-3 text-sm font-medium"
        >
          {bookingError}
        </div>
      )}

      <div className="relative grid min-h-[200px] gap-y-5">
        {startDate && endDate ? (
          <div
            data-testid="date-controls"
            className="font-maison-extended flex items-center justify-between text-base font-bold text-neutral-900 sm:justify-start"
          >
            <IconButton
              icon={IconChevronLeft}
              onClick={loadPrevPage}
              label="Back a week"
              disabled={!hasPrevPage}
              className="mr-2 h-8 w-8 rounded p-1 hover:enabled:bg-neutral-200 disabled:cursor-not-allowed disabled:text-neutral-300"
            />
            <span className="mr-3 sm:order-first">
              {startDate} - {endDate}
            </span>
            <IconButton
              icon={IconChevronRight}
              onClick={loadNextPage}
              label="Forward a week"
              className="h-8 w-8 rounded p-1 hover:enabled:bg-neutral-200 disabled:cursor-not-allowed disabled:text-neutral-300"
            />
          </div>
        ) : null}

        {A.isEmpty(slotsByDay) && !loading ? (
          <div>
            There are no appointments available for this week. Try a different
            time or{" "}
            <a href="/contact-us" className="font-semibold hover:underline">
              get in touch.
            </a>
          </div>
        ) : null}

        {slotsByDay.map(([day, slots]) => {
          const formattedDate = formatISOString(day, "dd LLL");
          const formattedDay = formatISOString(day, "EEEE");
          const isNextDay = slots[0].next_day_slot;

          const extraCost =
            isNextDay && !hidePrices
              ? scanningCenter.next_day_surcharge ?? null
              : null;

          const parsedDay = parse(
            day,
            DAY_FORMAT,
            new Date(1970, 0, 1, 0, 0, 0, 0)
          );

          return (
            <div
              ref={day === daySelected ? scrollToRef : null}
              data-testid="availability-days"
              className={classNames("space-y-3")}
              key={day}
            >
              {isNextDay ? (
                <div className="flex flex-wrap items-center gap-2">
                  <IconRapid className="h-5 w-5" />
                  <time dateTime={day} className={dateStyles}>
                    {isTomorrow(parsedDay) ? "Tomorrow" : formattedDay},{" "}
                    <span className="font-medium">{formattedDate}</span>
                  </time>

                  <button
                    type="button"
                    onClick={() => setShowNextDayModal(true)}
                    className="bg-warning-50 text-warning-900 ml-auto rounded-md px-1.5 py-0.5 font-medium sm:px-2 sm:py-1.5"
                  >
                    {!isNil(extraCost) && (
                      <span className="font-semibold">
                        +{formatCurrency(extraCost / 100)}{" "}
                      </span>
                    )}
                    next day <span className="max-sm:hidden">appointment</span>
                    <span className="sm:hidden">appt.</span>
                  </button>
                </div>
              ) : (
                <time dateTime={day} className={dateStyles}>
                  {formattedDay},{" "}
                  <span className="font-medium">{formattedDate}</span>
                </time>
              )}

              {!slots?.length && (
                <p className="col-span-full text-sm font-normal text-neutral-500">
                  No appointment available for this day.
                </p>
              )}

              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
                {slots.map((slot) => (
                  <AvailabilityItem
                    key={slot.start_time}
                    slot={slot}
                    onClick={() =>
                      dispatch({ type: "ShowConfirmationModal", slot })
                    }
                    selected={
                      state.confirmationModalVisible &&
                      state.selectedSlot === slot
                    }
                    nextDaySurcharge={extraCost}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {A.isNonEmpty(slotsByDay) ? (
          <div className="flex items-center justify-between gap-4 text-sm font-medium">
            <button
              type="button"
              disabled={!hasPrevPage}
              onClick={loadPrevPage}
              className="group flex items-center gap-4 text-neutral-900 disabled:text-neutral-400"
            >
              <IconChevronLeft className="h-7 w-7 rounded-lg bg-neutral-100 p-1 group-enabled:hover:bg-neutral-200" />
              Previous week
            </button>

            <button
              type="button"
              className="group flex items-center gap-4 text-neutral-900 disabled:text-neutral-400"
              onClick={loadNextPage}
            >
              Next week
              <IconChevronLeft className="h-7 w-7 rotate-180 rounded-lg bg-neutral-100 p-1 group-enabled:hover:bg-neutral-200" />
            </button>
          </div>
        ) : null}

        <Transition
          show={loading}
          as="div"
          enter="transition-opacity duration-75 delay-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="absolute inset-0 grid place-items-center bg-white/80"
        >
          <LoadingSpinner />
        </Transition>
      </div>
    </>
  );
};
